/**
 * 处理Markdown中的复合语法元素的完整性
 * 复合语法：列表+格式化、链接+格式化等组合情况
 */
import { LogDebugFunction } from '../utils/markdownTypes';

/**
 * 处理无序列表与粗体组合
 */
export const processUnorderedListWithBold = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 无序列表与粗体组合（"- **文本**"）
  if (/- +\*\*[^*]*$/.test(analyzedText)) {
    // 查找完整的无序列表+粗体项
    const pattern = /^(- +\*\*)([^*]*)(\*\*)([：:])?/;
    const match = remainingText.match(pattern);
    
    if (match) {
      // 计算完整的匹配长度
      const fullMatch = match[0].length;
      console.log('匹配到无序列表+粗体:', {
        匹配文本: match[0],
        前缀: match[1],
        内容: match[2],
        后缀: match[3],
        冒号: match[4] || '无',
        总长度: fullMatch
      });
      
      if (charsToShow < fullMatch) {
        return logDebugInfo('无序列表粗体-完整', fullMatch - charsToShow);
      }
    } else {
      // 如果没有找到完整的闭合结构，确保至少显示前缀+一个内容字符
      const prefixMatch = remainingText.match(/^(- +\*\*)/);
      if (prefixMatch) {
        const minRequired = prefixMatch[1].length + 1; // 加上至少一个内容字符
        if (charsToShow < minRequired) {
          return logDebugInfo('无序列表粗体-前缀', minRequired - charsToShow);
        }
      }
    }
    
    // 已经处理了这种模式，直接返回以避免继续处理基本语法
    return 0;
  }
  
  return 0;
};

/**
 * 处理无序列表与斜体组合
 */
export const processUnorderedListWithItalic = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 无序列表与单个星号（斜体）的组合 ("- *文本*")
  if (/- +\*(?!\*)[^*]*$/.test(analyzedText)) {
    const pattern = /^(- +\*)([^*]*)(\*)([：:])?/;
    const match = remainingText.match(pattern);
    
    if (match) {
      const fullMatch = match[0].length;
      console.log('匹配到无序列表+斜体:', {
        匹配文本: match[0],
        前缀: match[1],
        内容: match[2],
        后缀: match[3],
        冒号: match[4] || '无',
        总长度: fullMatch
      });
      
      if (charsToShow < fullMatch) {
        return logDebugInfo('无序列表斜体-完整', fullMatch - charsToShow);
      }
    } else {
      const prefixMatch = remainingText.match(/^(- +\*)/);
      if (prefixMatch) {
        const minRequired = prefixMatch[1].length + 1;
        if (charsToShow < minRequired) {
          return logDebugInfo('无序列表斜体-前缀', minRequired - charsToShow);
        }
      }
    }
    
    return 0;
  }
  
  return 0;
};

/**
 * 处理有序列表与粗体组合
 */
export const processOrderedListWithBold = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 有序列表与粗体组合 ("1. **文本**")
  if (/\d+\. +\*\*[^*]*$/.test(analyzedText)) {
    const pattern = /^(\d+\. +\*\*)([^*]*)(\*\*)([：:])?/;
    const match = remainingText.match(pattern);
    
    if (match) {
      const fullMatch = match[0].length;
      console.log('匹配到有序列表+粗体:', {
        匹配文本: match[0],
        前缀: match[1],
        内容: match[2],
        后缀: match[3],
        冒号: match[4] || '无',
        总长度: fullMatch
      });
      
      if (charsToShow < fullMatch) {
        return logDebugInfo('有序列表粗体-完整', fullMatch - charsToShow);
      }
    } else {
      const prefixMatch = remainingText.match(/^(\d+\. +\*\*)/);
      if (prefixMatch) {
        const minRequired = prefixMatch[1].length + 1;
        if (charsToShow < minRequired) {
          return logDebugInfo('有序列表粗体-前缀', minRequired - charsToShow);
        }
      }
    }
    
    return 0;
  }
  
  return 0;
};

/**
 * 处理缩进列表与粗体组合
 */
export const processIndentedListWithBold = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 缩进的无序列表与粗体组合
  if (/[ \t]+ *- +\*\*[^*]*$/.test(analyzedText)) {
    const pattern = /^([ \t]+ *- +\*\*)([^*]*)(\*\*)([：:])?/;
    const match = remainingText.match(pattern);
    
    if (match) {
      const fullMatch = match[0].length;
      console.log('匹配到二级无序列表+粗体:', {
        匹配文本: match[0],
        前缀: match[1],
        内容: match[2],
        后缀: match[3],
        冒号: match[4] || '无',
        总长度: fullMatch
      });
      
      if (charsToShow < fullMatch) {
        return logDebugInfo('二级无序列表粗体-完整', fullMatch - charsToShow);
      }
    } else {
      const prefixMatch = remainingText.match(/^([ \t]+ *- +\*\*)/);
      if (prefixMatch) {
        const minRequired = prefixMatch[1].length + 1; // 加上至少一个内容字符
        if (charsToShow < minRequired) {
          return logDebugInfo('二级无序列表粗体-前缀', minRequired - charsToShow);
        }
      }
    }
    
    return 0;
  }
  
  return 0;
};

/**
 * 处理缩进列表与斜体组合
 */
export const processIndentedListWithItalic = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 缩进的无序列表与斜体组合
  if (/[ \t]+ *- +\*(?!\*)[^*]*$/.test(analyzedText)) {
    const pattern = /^([ \t]+ *- +\*)([^*]*)(\*)([：:])?/;
    const match = remainingText.match(pattern);
    
    if (match) {
      const fullMatch = match[0].length;
      console.log('匹配到二级无序列表+斜体:', {
        匹配文本: match[0],
        前缀: match[1],
        内容: match[2],
        后缀: match[3],
        冒号: match[4] || '无',
        总长度: fullMatch
      });
      
      if (charsToShow < fullMatch) {
        return logDebugInfo('二级无序列表斜体-完整', fullMatch - charsToShow);
      }
    } else {
      const prefixMatch = remainingText.match(/^([ \t]+ *- +\*)/);
      if (prefixMatch) {
        const minRequired = prefixMatch[1].length + 1;
        if (charsToShow < minRequired) {
          return logDebugInfo('二级无序列表斜体-前缀', minRequired - charsToShow);
        }
      }
    }
    
    return 0;
  }
  
  return 0;
};

/**
 * 处理缩进有序列表与粗体组合
 */
export const processIndentedOrderedListWithBold = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 缩进的有序列表与粗体组合
  if (/[ \t]+ *\d+\. +\*\*[^*]*$/.test(analyzedText)) {
    const pattern = /^([ \t]+ *\d+\. +\*\*)([^*]*)(\*\*)([：:])?/;
    const match = remainingText.match(pattern);
    
    if (match) {
      const fullMatch = match[0].length;
      console.log('匹配到二级有序列表+粗体:', {
        匹配文本: match[0],
        前缀: match[1],
        内容: match[2],
        后缀: match[3],
        冒号: match[4] || '无',
        总长度: fullMatch
      });
      
      if (charsToShow < fullMatch) {
        return logDebugInfo('二级有序列表粗体-完整', fullMatch - charsToShow);
      }
    } else {
      const prefixMatch = remainingText.match(/^([ \t]+ *\d+\. +\*\*)/);
      if (prefixMatch) {
        const minRequired = prefixMatch[1].length + 1;
        if (charsToShow < minRequired) {
          return logDebugInfo('二级有序列表粗体-前缀', minRequired - charsToShow);
        }
      }
    }
    
    return 0;
  }
  
  return 0;
}; 